<script setup lang="ts">
import { computed } from 'vue'
import moment from 'moment'
import { BaseButton, BaseMenuLite, BaseDatePicker } from '@/components/base'
import { CalendarViewType, CalendarViewLabelType } from '@/enum'
import { VSwitch } from 'vuetify/components'

const views = [
  {
    label: CalendarViewLabelType.MONTH,
    value: CalendarViewType.MONTH,
    icon: 'mdi-calendar-month'
  },
  {
    label: CalendarViewLabelType.WEEK,
    value: CalendarViewType.WEEK,
    icon: 'mdi-calendar-week'
  },
  {
    label: CalendarViewLabelType.DAY,
    value: CalendarViewType.DAY,
    icon: 'mdi-calendar-today'
  }
  // {
  //   label: CalendarViewLabelType.LIST,
  //   value: CalendarViewType.LIST,
  //   icon: 'mdi-format-list-bulleted'
  // }
]

const currentDate = defineModel({ default: new Date() })
const activeView = defineModel<CalendarViewType>('activeView', { default: CalendarViewType.MONTH })
const isCondensedEvents = defineModel('isCondensedEvents', { default: false })
const emit = defineEmits(['prev', 'next', 'update', 'today', 'change:view'])

const currentCalendarLabel = computed(() => {
  switch (activeView.value) {
    case CalendarViewType.MONTH:
      return moment(currentDate.value).format('MMMM YYYY')
    case CalendarViewType.WEEK:
      return `${moment(currentDate.value).startOf('isoWeek').format('MMM DD')} - ${moment(currentDate.value).endOf('isoWeek').format('MMM DD, YYYY')}`
    case CalendarViewType.DAY:
      return moment(currentDate.value).format('ddd, MMM DD')
    default:
      return moment(currentDate.value).format('MMMM YYYY')
  }
})

const updateActiveView = (view: any) => {
  const previous = activeView.value
  activeView.value = view.value
  emit('change:view', view.value, previous)
}

const handlePrevious = () => {
  switch (activeView.value) {
    case CalendarViewType.MONTH:
      currentDate.value = moment(currentDate.value).subtract(1, 'month').startOf('month').toDate()
      break
    case CalendarViewType.WEEK:
      currentDate.value = moment(currentDate.value).subtract(1, 'week').toDate()
      break
    case CalendarViewType.DAY:
      currentDate.value = moment(currentDate.value).subtract(1, 'day').toDate()
      break
    default:
      currentDate.value = moment(currentDate.value).subtract(1, 'month').toDate()
      break
  }
  emit('prev')
  emit('update')
}

const handleNext = () => {
  switch (activeView.value) {
    case CalendarViewType.MONTH:
      currentDate.value = moment(currentDate.value).add(1, 'month').startOf('month').toDate()
      break
    case CalendarViewType.WEEK:
      currentDate.value = moment(currentDate.value).add(1, 'week').toDate()
      break
    case CalendarViewType.DAY:
      currentDate.value = moment(currentDate.value).add(1, 'day').toDate()
      break
    default:
      currentDate.value = moment(currentDate.value).add(1, 'month').toDate()
      break
  }
  emit('next')
  emit('update')
}

const handleToday = () => {
  currentDate.value = new Date()
  emit('today')
  emit('update')
}
</script>
<template>
  <div class="calendar-header d-flex pa-5 align-center ga-5 position-relative z-1">
    <BaseButton class="body-1--new medium border-gray" variant="outlined" @click="handleToday"
      >Today</BaseButton
    >
    <v-icon icon="mdi-chevron-left" size="24" @click="handlePrevious" />
    <slot>
      <BaseDatePicker no-range disabled-apply v-model="currentDate" @change="() => emit('update')">
        <template #default="{ on }">
          <BaseButton class="body-1--new medium pl-3 pr-2 border-gray" variant="outlined" v-on="on"
            >{{ currentCalendarLabel }}<v-icon class="ml-2 mb-1" size="24" icon="mdi-chevron-down"
          /></BaseButton>
        </template>
      </BaseDatePicker>
    </slot>
    <v-icon icon="mdi-chevron-right" size="24" @click="handleNext" />
    <v-spacer />
    <div class="d-flex align-center ga-5">
      <span :class="{ 'text-slate_400': isCondensedEvents }">Normal</span>
      <v-switch
        color="orange_500"
        hide-details
        v-model="isCondensedEvents"
        density="compact"
        inset
        inline
      />
      <span :class="{ 'text-slate_400': !isCondensedEvents }">Compact</span>
    </div>
    <v-spacer />
    <!-- <BaseTabs
      outlined
      no-tab-state-preservation
      :tab-items="views"
      v-model="activeView"
      class="body-2"
      @change="(current, previous) => emit('change:view', current, previous)"
    /> -->
    <BaseMenuLite
      class="body-2"
      content-class="body-1--new mt-1"
      :item-height="40"
      :min-width="120"
      :items="views"
      icon-position="left"
      @change="updateActiveView"
    >
      <template #menu-activator="{ on, open }">
        <BaseButton
          class="border-sm pr-2 border-black body-1--new medium"
          height-variant="auto"
          min-width="max-content"
          v-bind="$attrs"
          v-on="on"
        >
          {{
            CalendarViewLabelType[
              CalendarViewType[activeView] as keyof typeof CalendarViewLabelType
            ]
          }}
          <v-icon :class="`${open ? 'rotate-n180' : ''} ml-2`" size="24" icon="mdi-chevron-down" />
        </BaseButton>
      </template>
    </BaseMenuLite>
  </div>
</template>
